import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useEvents } from "../../Contexts/EventsContext";
import { auth } from "../../Services/firebase-config";
import useDeadlineModal, { DeadlineModal } from "../../hooks/useDeadlineModal";
import {
  formatDeadlineDisplay,
  hasDeadlinePassed,
  calculateCountdown,
} from "../helpers/helpers";
import ParticipationFormModal from "./ParticipationForm";

// ─── Animated SVG background ─────────────────────────────────────────────────
const BackgroundLines = () => (
  <svg
    className="pointer-events-none fixed inset-0 w-full h-full"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid slice"
  >
    <defs>
      <linearGradient id="line1" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop
          offset="0%"
          stopColor="var(--color-custom-green-1, #60D3AA)"
          stopOpacity="0"
        />
        <stop
          offset="40%"
          stopColor="var(--color-custom-green-1, #60D3AA)"
          stopOpacity="0.12"
        />
        <stop
          offset="100%"
          stopColor="var(--color-custom-green-1, #60D3AA)"
          stopOpacity="0"
        />
      </linearGradient>
      <linearGradient id="line2" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop
          offset="0%"
          stopColor="var(--color-custom-green-1, #60D3AA)"
          stopOpacity="0"
        />
        <stop
          offset="60%"
          stopColor="var(--color-custom-green-1, #60D3AA)"
          stopOpacity="0.07"
        />
        <stop
          offset="100%"
          stopColor="var(--color-custom-green-1, #60D3AA)"
          stopOpacity="0"
        />
      </linearGradient>
    </defs>
    <path
      d="M -100 250 C 150 100, 400 400, 650 200 S 1100 50, 1400 300 S 1700 500, 2000 250"
      fill="none"
      stroke="url(#line1)"
      strokeWidth="1.5"
    />
    <path
      d="M -100 450 C 200 300, 500 600, 800 380 S 1200 150, 1500 420 S 1800 600, 2100 380"
      fill="none"
      stroke="url(#line2)"
      strokeWidth="1"
    />
    <path
      d="M -100 650 C 300 500, 600 780, 900 560 S 1300 320, 1600 580 S 1900 720, 2200 520"
      fill="none"
      stroke="url(#line1)"
      strokeWidth="0.8"
    />
    <path
      d="M 200 -50 C 300 200, 150 500, 350 700 S 500 900, 400 1100"
      fill="none"
      stroke="url(#line2)"
      strokeWidth="1"
    />
    <path
      d="M 900 -50 C 1000 250, 750 500, 950 750 S 1100 950, 950 1150"
      fill="none"
      stroke="url(#line1)"
      strokeWidth="0.7"
    />
  </svg>
);

// ─── Mini countdown display ───────────────────────────────────────────────────
const CountdownBadge = ({ deadline }) => {
  const [countdown, setCountdown] = useState(calculateCountdown(deadline));

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(calculateCountdown(deadline));
    }, 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  const { days, hours, minutes, seconds } = countdown;
  const isUrgent = days < 3;

  return (
    <div
      className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border ${
        isUrgent
          ? "bg-red-50 border-red-200 text-red-600"
          : "bg-custom-green-1/10 border-custom-green-1/30 text-custom-green-1"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
          isUrgent ? "bg-red-500 animate-pulse" : "bg-custom-green-1"
        }`}
      />
      {days > 0 ? (
        <span>
          {days}d {hours}h left
        </span>
      ) : (
        <span>
          {hours}h {minutes}m {seconds}s
        </span>
      )}
    </div>
  );
};

// ─── Event Card ───────────────────────────────────────────────────────────────
const EventCard = ({ event, onClosed, onEnter, currentUserId, onApply }) => {
  const passed = hasDeadlinePassed(event.deadline);
  const isParticipant =
    currentUserId && Array.isArray(event.participantIds)
      ? event.participantIds.includes(currentUserId)
      : false;

  const handleActionClick = () => {
    if (isParticipant) {
      onEnter(event);
    } else if (passed) {
      onClosed(event);
    } else {
      onApply(event);
    }
  };

  return (
    <div className="group relative flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
      {/* ── Image ────────────────────────────────────────────── */}
      <div className="relative h-44 overflow-hidden bg-gray-50 flex-shrink-0">
        {event.image ? (
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-custom-green-1/10 to-custom-green-1/5">
            <span className="text-4xl opacity-25">🎮</span>
          </div>
        )}

        {/* Overlay badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider bg-white/90 backdrop-blur-sm text-gray-600 px-2.5 py-1 rounded-full border border-white/60 shadow-sm">
            {event.eventType}
          </span>
          {event.isFeatured && (
            <span className="text-[10px] font-semibold bg-custom-green-1 text-white px-2.5 py-1 rounded-full shadow-sm">
              ✦ Featured
            </span>
          )}
        </div>

        {/* Closed dimmer */}
        {passed && (
          <div className="absolute inset-0 bg-gray-900/30 flex items-center justify-center">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white bg-gray-900/60 px-3 py-1.5 rounded-full backdrop-blur-sm">
              Closed
            </span>
          </div>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 p-5 gap-3">
        <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 group-hover:text-custom-green-1 transition-colors duration-200">
          {event.title}
        </h3>

        <p className="text-gray-400 text-xs leading-relaxed line-clamp-3 flex-1">
          {event.description}
        </p>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-gray-400 text-xs">
          <svg
            className="w-3.5 h-3.5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="truncate">{event.location}</span>
        </div>

        {/* Date */}
        <div className="flex items-center gap-1.5 text-gray-400 text-xs">
          <svg
            className="w-3.5 h-3.5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>{event.date}</span>
        </div>

        <div className="border-t border-gray-100" />

        {/* ── Deadline row ──────────────────────────────────── */}
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
            {passed ? "Ended" : "Deadline"}
          </span>
          {!passed ? (
            <CountdownBadge deadline={event.deadline} />
          ) : (
            <span className="text-xs text-gray-400">
              {formatDeadlineDisplay(event.deadline, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          )}
        </div>

        {/* ── CTA button ────────────────────────────────────── */}
        <button
          onClick={handleActionClick}
          className={`w-full text-xs font-semibold px-4 py-2.5 rounded-xl transition-all duration-200 ${
            isParticipant
              ? "bg-custom-green-1 text-white hover:opacity-90 shadow-sm"
              : passed
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "border border-custom-green-1 text-custom-green-1 hover:bg-custom-green-1 hover:text-white"
          }`}
        >
          {isParticipant ? "Enter →" : passed ? "Closed" : "Apply Now"}
        </button>
      </div>
    </div>
  );
};

// ─── Filter Pill ──────────────────────────────────────────────────────────────
const FilterTab = ({ label, active, count, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
      active
        ? "bg-custom-green-1 text-white shadow-sm"
        : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
    }`}
  >
    {label}
    <span
      className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
        active ? "bg-white/25 text-white" : "bg-gray-200 text-gray-500"
      }`}
    >
      {count}
    </span>
  </button>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function EventsPage() {
  const { events } = useEvents();
  const navigate = useNavigate();
  const currentUserId = auth.currentUser?.uid;
  const { isOpen, currentEvent, openModal, closeModal } = useDeadlineModal();

  const [filter, setFilter] = useState("all");
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [applyEvent, setApplyEvent] = useState(null);

  // ── Filter logic ───────────────────────────────────────────────────────────
  const filteredEvents = (events || []).filter((event) => {
    const passed = hasDeadlinePassed(event.deadline);
    if (filter === "upcoming") return !passed;
    if (filter === "past") return passed;
    return true;
  });

  const upcomingCount = (events || []).filter(
    (e) => !hasDeadlinePassed(e.deadline),
  ).length;
  const pastCount = (events || []).filter((e) =>
    hasDeadlinePassed(e.deadline),
  ).length;

  // ── Scroll state ───────────────────────────────────────────────────────────
  const updateScrollState = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = carouselRef.current;
    el?.addEventListener("scroll", updateScrollState, { passive: true });
    return () => el?.removeEventListener("scroll", updateScrollState);
  }, [filteredEvents, updateScrollState]);

  const scroll = (dir) => {
    carouselRef.current?.scrollBy({
      left: dir === "left" ? -340 : 340,
      behavior: "smooth",
    });
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleEnter = (event) => {
    navigate(`/hackathon-dashboard?eventId=${event.id}`, {
      state: { eventId: event.id },
    });
  };

  const handleClosed = (event) => {
    openModal(event);
  };

  return (
    <div className="relative min-h-screen bg-white overflow-x-hidden">
      {/* ── Background decoration ──────────────────────────────────────── */}
      <BackgroundLines />
      <div
        className="pointer-events-none absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-15 blur-3xl -z-0"
        style={{
          background:
            "radial-gradient(circle, var(--color-custom-green-1, #60D3AA), transparent 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10 blur-3xl -z-0"
        style={{
          background:
            "radial-gradient(circle, var(--color-custom-green-1, #60D3AA), transparent 100%)",
        }}
      />

      {/* ── Content ────────────────────────────────────────────────────── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-custom-green-1 mb-2">
            STI Secretariat
          </p>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
            Events & Hackathons
          </h1>
          <p className="text-gray-400 text-sm max-w-lg">
            Competitions, bootcamps, and innovation sprints shaping Uganda's
            technology future.
          </p>
        </div>

        {/* Filters + Arrows */}
        <div className="flex items-center justify-between mb-7 gap-4 flex-wrap">
          <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-full border border-gray-200">
            <FilterTab
              label="All"
              active={filter === "all"}
              count={events?.length ?? 0}
              onClick={() => setFilter("all")}
            />
            <FilterTab
              label="Upcoming"
              active={filter === "upcoming"}
              count={upcomingCount}
              onClick={() => setFilter("upcoming")}
            />
            <FilterTab
              label="Past"
              active={filter === "past"}
              count={pastCount}
              onClick={() => setFilter("past")}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 hover:border-custom-green-1 hover:text-custom-green-1 disabled:opacity-25 disabled:cursor-not-allowed transition-all duration-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 hover:border-custom-green-1 hover:text-custom-green-1 disabled:opacity-25 disabled:cursor-not-allowed transition-all duration-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Carousel */}
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-28 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center mb-4 text-2xl">
              📭
            </div>
            <p className="text-gray-400 text-sm">
              No {filter !== "all" ? filter : ""} events right now.
            </p>
          </div>
        ) : (
          <div className="relative">
            <div
              ref={carouselRef}
              className="flex gap-5 overflow-x-auto pb-4 scroll-smooth"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {filteredEvents.map((event) => (
                <div key={event.id} className="flex-shrink-0 w-72">
                  <EventCard
                    event={event}
                    currentUserId={currentUserId}
                    onEnter={handleEnter}
                    onClosed={handleClosed}
                    onApply={(e) => setApplyEvent(e)}
                  />
                </div>
              ))}
              {/* trailing spacer */}
              <div className="flex-shrink-0 w-6" />
            </div>

            {/* Right fade */}
            {canScrollRight && (
              <div
                className="pointer-events-none absolute right-0 top-0 h-full w-20"
                style={{
                  background:
                    "linear-gradient(to left, rgba(255,255,255,0.95), transparent)",
                }}
              />
            )}
          </div>
        )}
      </div>

      <ParticipationFormModal
        isOpen={!!applyEvent}
        event={applyEvent}
        onClose={() => setApplyEvent(null)}
        onSuccess={() => {
          setApplyEvent(null);
          // optionally show a toast: "Application submitted!"
        }}
      />

      {/* Deadline Modal */}
      <DeadlineModal
        isOpen={isOpen}
        event={currentEvent}
        deadline={formatDeadlineDisplay(currentEvent?.deadline)}
        onClose={closeModal}
      />
    </div>
  );
}
